import { z } from 'zod';

/**
 * Persisted "Ask AI" conversations.
 *
 * The v1 assistant was stateless — the client replayed the whole history each
 * turn and a reload lost it. These types cover threads that survive, plus the
 * streaming and file-attachment surface.
 *
 * (Owned by the AI-chat work — extend freely.)
 */

export const aiConversationSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  titleGenerated: z.boolean(),
  /** Non-null once archived; archived threads are hidden from the default list. */
  archivedAt: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Convenience for the thread list; not stored. */
  messageCount: z.number().optional(),
  /** First line of the most recent message — the list's subtitle. Not stored. */
  preview: z.string().optional(),
});
export type AiConversation = z.infer<typeof aiConversationSchema>;

export const aiStoredMessageSchema = z.object({
  id: z.string().uuid(),
  conversationId: z.string().uuid(),
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  toolsUsed: z.array(z.string()),
  companiesAccessed: z.array(z.string()),
  attachedDocumentIds: z.array(z.string()),
  createdAt: z.string(),
});
export type AiStoredMessage = z.infer<typeof aiStoredMessageSchema>;

export const createAiConversationInputSchema = z.object({
  title: z.string().min(1).max(200).optional(),
});
export type CreateAiConversationInput = z.infer<typeof createAiConversationInputSchema>;

export const renameAiConversationInputSchema = z.object({
  title: z.string().min(1).max(200),
});
export type RenameAiConversationInput = z.infer<typeof renameAiConversationInputSchema>;

/**
 * Rename and/or archive in one call. A hand-typed title also sets
 * `titleGenerated`, which is what stops the auto-titler from ever overwriting
 * a name the owner chose.
 */
export const updateAiConversationInputSchema = z
  .object({
    title: z.string().min(1).max(200).optional(),
    archived: z.boolean().optional(),
  })
  .refine((v) => v.title !== undefined || v.archived !== undefined, {
    message: 'Provide title and/or archived',
  });
export type UpdateAiConversationInput = z.infer<typeof updateAiConversationInputSchema>;

/** A thread plus its full message history, in order. */
export interface AiConversationDetail {
  conversation: AiConversation;
  messages: AiStoredMessage[];
}

/**
 * A vault document offered in the chat's attach picker. Deliberately a slim
 * projection — the chat never ships file bytes to the browser, only enough to
 * recognise the document.
 */
export const aiAttachableDocumentSchema = z.object({
  id: z.string().uuid(),
  companyId: z.string().uuid(),
  companyName: z.string().nullable(),
  fileName: z.string(),
  docType: z.string().nullable(),
  documentDate: z.string().nullable(),
  vendorName: z.string().nullable(),
  amount: z.string().nullable(),
  summary: z.string().nullable(),
  /** False when extraction produced no text — the model will only see metadata. */
  hasText: z.boolean(),
});
export type AiAttachableDocument = z.infer<typeof aiAttachableDocumentSchema>;

export const sendAiMessageInputSchema = z.object({
  content: z.string().min(1).max(8000),
  /** Vault document ids to put in front of the model for this turn. */
  attachedDocumentIds: z.array(z.string().uuid()).max(5).optional(),
});
export type SendAiMessageInput = z.infer<typeof sendAiMessageInputSchema>;

/**
 * One frame of a streamed reply, newline-delimited JSON over the response body.
 * `delta` carries text as it is produced; `tool` announces a lookup so the UI
 * can show what the assistant is doing rather than an opaque spinner.
 */
export type AiStreamFrame =
  | { type: 'delta'; text: string }
  | { type: 'tool'; name: string }
  /** The persisted id of the user's turn, emitted before the model is called. */
  | { type: 'user-message'; messageId: string }
  /** Auto-generated thread title, emitted after the first exchange only. */
  | { type: 'title'; title: string }
  | {
      type: 'done';
      messageId: string;
      toolsUsed: string[];
      companiesAccessed: string[];
      /** True when the client stopped the turn; the partial text was still saved. */
      stopped?: boolean;
    }
  | { type: 'error'; message: string };
